import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import Logo from "./Logo";
import { NAV_LINKS, SITE } from "../lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const reduce = useReducedMotion();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  const linkCls = ({ isActive }) =>
    `relative whitespace-nowrap text-[0.92rem] font-medium transition-colors duration-200 hover:text-forest ${
      isActive ? "text-forest stitch-underline" : "text-charcoal/70"
    }`;

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 border-b bg-warmwhite/90 backdrop-blur-md transition-[background-color,border-color,box-shadow] duration-300 ${
        scrolled ? "border-inkline shadow-[0_2px_20px_rgba(48,50,47,0.06)]" : "border-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-site items-center justify-between gap-6 px-4 sm:px-6 lg:px-12">
        <Link to="/" data-testid="nav-brand-logo" aria-label="CareCraft home" className="shrink-0">
          <Logo />
        </Link>

        <nav className="hidden items-center gap-6 xl:flex" aria-label="Primary">
          {NAV_LINKS.map((l) => (
            <NavLink key={l.to} to={l.to} className={linkCls} data-testid={l.testId}>
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={SITE.phoneHref}
            data-testid="nav-phone-link"
            className="hidden items-center gap-2 whitespace-nowrap text-[0.92rem] font-medium text-forest transition-colors hover:text-clay md:inline-flex"
          >
            <Phone className="h-4 w-4" aria-hidden="true" />
            {SITE.phoneDisplay}
          </a>
          <Link
            to="/contact"
            data-testid="nav-cta-talk-with-doris"
            className="hidden min-h-[44px] items-center whitespace-nowrap rounded-full bg-forest px-6 py-2.5 text-[0.92rem] font-medium text-ivory transition-[background-color,transform] duration-300 hover:-translate-y-0.5 hover:bg-forest-dark sm:inline-flex"
          >
            Talk With Doris
          </Link>
          <button
            type="button"
            data-testid="nav-mobile-toggle"
            className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-inkline text-forest xl:hidden"
            aria-expanded={open}
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen((v) => !v)}
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-x-0 top-[72px] bottom-0 z-40 overflow-y-auto bg-forest grain"
            initial={reduce ? false : { opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={reduce ? undefined : { opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <nav className="mx-auto flex max-w-site flex-col gap-1 px-6 py-10" aria-label="Mobile">
              {NAV_LINKS.map((l, i) => (
                <motion.div
                  key={l.to}
                  initial={reduce ? false : { opacity: 0, x: -12 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 + i * 0.04, duration: 0.35 }}
                >
                  <NavLink
                    to={l.to}
                    data-testid={l.testId}
                    className={({ isActive }) =>
                      `block border-b border-ivory/10 py-4 font-serif text-3xl ${
                        isActive ? "text-clay" : "text-ivory"
                      }`
                    }
                  >
                    {l.label}
                  </NavLink>
                </motion.div>
              ))}
              <div className="mt-8 flex flex-col gap-4">
                <Link
                  to="/contact"
                  data-testid="mobile-menu-cta-talk-with-doris"
                  className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-ivory px-6 py-3 font-medium text-forest"
                >
                  Talk With Doris <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <a
                  href={SITE.phoneHref}
                  data-testid="mobile-menu-phone"
                  className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full border border-ivory/30 px-6 py-3 font-medium text-ivory"
                >
                  <Phone className="h-4 w-4" aria-hidden="true" /> {SITE.phoneDisplay}
                </a>
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
